import { useEffect, useRef } from "react";
import { createContext, useContext } from "react";
import BrowserInteractionTime from "browser-interaction-time";
import { updateAgentPanelTime } from "../API/agentApis";
import { format } from "date-fns";
import useAuthStore from "../store/authStore";
import useAgent from "../hooks/useAgent";
import useGeneralStore from "../store/generalStore"; // 1. IMPORTED

const AGENT_PANEL_TIME_LOCAL_KEY = "agent_panel_time_tracker";
const AGENT_PANEL_TIME_LOCAL_TIMESTAMP = "agent_panel_time_tracker_timestamp";
const TOTAL_AGENT_PANEL_TIME_TODAY = "total_agent_panel_time_today";

const agentContext = createContext();

export const useAgentContext = () => useContext(agentContext);

export function AgentContextProvider({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const showAlert = useGeneralStore((state) => state.open_alert); 

  useAgent();

  const browserInteractionTime = useRef(null);

  const getTodaysPanelKey = () => format(new Date(), "dd_MM_yyyy");
  const parsePanelTimeLocal = (prev_time) =>
    Number(!!prev_time ? JSON.parse(prev_time)?.[getTodaysPanelKey()] : 0);

  async function updatePanelTime(panel_time) {
    try {
      // total time updation -> local storage
      let prev_total_panel_time = localStorage.getItem(
        TOTAL_AGENT_PANEL_TIME_TODAY
      );
      localStorage.setItem(
        TOTAL_AGENT_PANEL_TIME_TODAY,
        JSON.stringify({
          [getTodaysPanelKey()]: panel_time + parsePanelTimeLocal(prev_total_panel_time),
        })
      );

      const res = await updateAgentPanelTime({ panel_time, idle_time: null });
      // setPanelTime(!!res?.panel_time ? Number(res?.panel_time) : 0);
    } catch (err) {
      console.log(err);
    }
  }

  async function triggerPanelTimeTracker() {
    let prevPanelTrackerTimeStamp = new Date();
    let currentPanelTrackerTimeStamp = new Date();

    // check for item in localstorage
    const localStoragePanelTrackerTimeStamp = localStorage.getItem(
      AGENT_PANEL_TIME_LOCAL_TIMESTAMP
    );
    if (!!localStoragePanelTrackerTimeStamp)
      prevPanelTrackerTimeStamp = new Date(
        JSON.parse(localStoragePanelTrackerTimeStamp)
      );
    else
      localStorage.setItem(
        AGENT_PANEL_TIME_LOCAL_TIMESTAMP,
        JSON.stringify(currentPanelTrackerTimeStamp)
      );

    // API update logic => every 5 mins
    let FIVE_MINUTES = 5 * 60 * 1000; // 5min * 60sec * 1000msec
    let time_tracked = parseInt(
      browserInteractionTime.current.getTimeInMilliseconds() / 1000
    );

    // total panel time updation -> state
    // let prev_total_panel_time = localStorage.getItem(TOTAL_AGENT_PANEL_TIME_TODAY);
    // setPanelTime(time_tracked + parsePanelTimeLocal(prev_total_panel_time));

    if (
      currentPanelTrackerTimeStamp.getTime() -
        prevPanelTrackerTimeStamp.getTime() >=
      FIVE_MINUTES
    ) {
      await updatePanelTime(time_tracked);
      localStorage.setItem(AGENT_PANEL_TIME_LOCAL_KEY, "0");
      browserInteractionTime.current.reset();
      browserInteractionTime.current.startTimer();
      localStorage.setItem(
        AGENT_PANEL_TIME_LOCAL_TIMESTAMP,
        JSON.stringify(currentPanelTrackerTimeStamp)
      );
    } else {
      localStorage.setItem(AGENT_PANEL_TIME_LOCAL_KEY, time_tracked);
    }
  }

  async function startPanelTimeTracker() {
    localStorage.setItem(
      AGENT_PANEL_TIME_LOCAL_TIMESTAMP,
      JSON.stringify(new Date())
    );

    // total panel time -> initialization
    // let prev_total_panel_time = localStorage.getItem(TOTAL_AGENT_PANEL_TIME_TODAY);
    // setPanelTime(parsePanelTimeLocal(prev_total_panel_time));

    const localPanelTrackerTime = localStorage.getItem(
      AGENT_PANEL_TIME_LOCAL_KEY
    );
    if (!!localPanelTrackerTime)
      await updatePanelTime(parseInt(localPanelTrackerTime));

    if (!!browserInteractionTime.current)
      browserInteractionTime.current?.destroy();

    browserInteractionTime.current = new BrowserInteractionTime({
      timeIntervalEllapsedCallbacks: [],
      absoluteTimeEllapsedCallbacks: [],
      browserTabInactiveCallbacks: [],
      browserTabActiveCallbacks: [],
      idleTimeoutMs: 600000, // 10 mins = 10m * 60s * 1000ms
      checkCallbacksIntervalMs: 5000, // every 5 sec updated in localStorage
    });

    browserInteractionTime.current.startTimer();

    const interval_callback = {
      multiplier: (time) => time,
      timeInMilliseconds: 1000,
      callback: () => triggerPanelTimeTracker(),
    };
    browserInteractionTime.current.addTimeIntervalEllapsedCallback(
      interval_callback
    );
  }

  async function triggerPanelTimeTrackerLogout() {
    let time_tracked = parseInt(
      browserInteractionTime.current.getTimeInMilliseconds() / 1000
    );
    await updatePanelTime(time_tracked);
    localStorage.setItem(AGENT_PANEL_TIME_LOCAL_KEY, "0");
    browserInteractionTime.current.destroy();
  }

  useEffect(() => {
    // 3. UPDATED LOGIC
    if (isAuthenticated) {
      // Check if permissions are 'default' (not yet asked)
      if ("Notification" in window && Notification.permission === "default") {
        
        const handleRequestPermission = () => {
          Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              showAlert({
                variant: "success",
                msg: "Browser notifications enabled!",
              });
            } else {
              showAlert({
                variant: "warning",
                msg: "Browser notifications were not enabled. You can enable them from site settings later.",
              });
            }
          });
        };

        // Use your existing showAlert to ask the user to opt-in
        showAlert({
          variant: "info",
          persist: true,
          msg: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "20px",
                width: "100%",
              }}
            >
              <span>Do you want to receive browser notifications for calls?</span>
              <button
                onClick={handleRequestPermission}
                style={{
                  padding: "5px 10px",
                  cursor: "pointer",
                  border: "none",
                  borderRadius: "4px",
                  background: "#fff",
                  color: "#000",
                  fontWeight: "bold",
                }}
              >
                Enable
              </button>
            </div>
          ),
        });
      }

      startPanelTimeTracker();
    }
    
    if (!isAuthenticated) {
      // 4. ADDED SAFETY CHECK
      if (browserInteractionTime.current) {
        triggerPanelTimeTrackerLogout();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const values = {};

  return (
    <agentContext.Provider value={values}>{children}</agentContext.Provider>
  );
}